import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { formatGel, variantLabel } from '../../sellerMeta';
import type { DiscountSummary } from './useDiscountDialog';

type DiscountDialogSummaryProps = {
    summary: DiscountSummary;
};

/**
 * What the dialog discounts: the line's name (with its variant when the
 * product has several), `quantity × list price` and its list total; or the
 * whole sale, its item count and what the lines leave. A secondary `Card`
 * (the design system's surface, radius and padding).
 */
export const DiscountDialogSummary: React.FC<DiscountDialogSummaryProps> = ({
    summary,
}) => {
    const { t } = useTranslation('pages');

    const label =
        summary.kind === 'line'
            ? (() => {
                  const { product, variant } = summary.line;
                  const variantText = variantLabel(product, variant);
                  return variantText === null
                      ? product.name
                      : t('seller.cartLineVariant', {
                            product: product.name,
                            variant: variantText,
                        });
              })()
            : t('seller.discount.wholeSale');
    const detail =
        summary.kind === 'line'
            ? t('seller.discount.lineQuantity', {
                  quantity: summary.quantity,
                  price: formatGel(summary.unitPrice),
              })
            : t('seller.discount.saleItems', { count: summary.itemCount });
    const amount =
        summary.kind === 'line' ? summary.listTotal : summary.afterLines;

    return (
        <Card
            className="flex-row items-center justify-between gap-3"
            {...getNodeTestIdProp('seller-discount-summary')}
            variant="secondary"
        >
            <div className="flex min-w-0 flex-col">
                <Typography truncate type="body-sm" weight="semibold">
                    {label}
                </Typography>
                <Typography
                    className="tabular-nums"
                    color="muted"
                    type="body-xs"
                >
                    {detail}
                </Typography>
            </div>
            <Typography
                className="shrink-0 tabular-nums"
                type="body-sm"
                weight="semibold"
            >
                {formatGel(amount)}
            </Typography>
        </Card>
    );
};
